// instagram-auth.service.ts
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

// Type declarations for Facebook SDK
declare const FB: any;

export interface InstagramAccount {
  pageName: string;
  pageId: string;
  pageAccessToken: string;
  instagramAccountId: string;
  username?: string;
  accountType?: string;
}

export interface FacebookPage {
  id: string;
  name: string;
  access_token: string;
  category: string;
}

export interface AuthStatus {
  isLoggedIn: boolean;
  user?: {
    id: string;
    name: string;
    email?: string;
  };
  error?: string;
  isLoading?: boolean;
}

export interface FacebookAuthResponse {
  status: "connected" | "not_authorized" | "unknown";
  authResponse?: {
    accessToken: string;
    userID: string;
    expiresIn: number;
    signedRequest: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class InstagramAuthService {
  // Replace with your actual Facebook App ID
  private readonly FB_APP_ID = "YOUR_FACEBOOK_APP_ID";
  private readonly API_VERSION = "v19.0";
  private readonly REQUIRED_PERMISSIONS = [
    "pages_read_engagement",
    "pages_show_list",
    "instagram_basic",
    "instagram_content_publish",
  ];

  // Observables for state management
  private authStatusSubject = new BehaviorSubject<AuthStatus>({
    isLoggedIn: false,
    isLoading: true,
  });
  private instagramAccountsSubject = new BehaviorSubject<InstagramAccount[]>(
    []
  );
  private facebookPagesSubject = new BehaviorSubject<FacebookPage[]>([]);

  // Public observables
  public authStatus$ = this.authStatusSubject.asObservable();
  public instagramAccounts$ = this.instagramAccountsSubject.asObservable();
  public facebookPages$ = this.facebookPagesSubject.asObservable();

  // Private state
  private isSDKLoaded = false;
  private currentAccessToken: string | null = null;
  private currentUser: any = null;

  constructor() {
    this.initializeFacebookSDK();
  }

  // Initialize Facebook SDK
  private initializeFacebookSDK(): void {
    if (typeof window === "undefined") {
      console.warn("Facebook SDK can only be loaded in browser environment");
      return;
    }

    // Check if SDK is already loaded
    if ((window as any).FB) {
      this.isSDKLoaded = true;
      this.checkLoginStatus();
      return;
    }

    // Load Facebook SDK
    (window as any).fbAsyncInit = () => {
      FB.init({
        appId: this.FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: this.API_VERSION,
      });

      this.isSDKLoaded = true;
      this.checkLoginStatus();
    };

    // Inject Facebook SDK script
    if (!document.getElementById("facebook-sdk")) {
      const script = document.createElement("script");
      script.id = "facebook-sdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }
  }

  // Check current login status
  private checkLoginStatus(): void {
    if (!this.isSDKLoaded) return;

    FB.getLoginStatus((response: FacebookAuthResponse) => {
      console.log("Facebook login status:", response);
      this.handleAuthResponse(response);
    });
  }

  // Handle authentication response
  private handleAuthResponse(response: FacebookAuthResponse): void {
    this.authStatusSubject.next({
      ...this.authStatusSubject.value,
      isLoading: false,
    });

    if (response.status === "connected" && response.authResponse) {
      this.currentAccessToken = response.authResponse.accessToken;
      this.fetchUserInfo();
    } else {
      const errorMessage =
        response.status === "not_authorized"
          ? "Please authorize the app to continue"
          : "Please log in to Facebook to continue";

      this.authStatusSubject.next({
        isLoggedIn: false,
        error: errorMessage,
        isLoading: false,
      });
    }
  }

  // Fetch user information from Facebook
  private fetchUserInfo(): void {
    if (!this.currentAccessToken) return;

    FB.api("/me", { fields: "id,name,email" }, (response: any) => {
      if (response.error) {
        console.error("Error fetching user info:", response.error);
        this.authStatusSubject.next({
          isLoggedIn: false,
          error: "Failed to get user information",
          isLoading: false,
        });
        return;
      }

      this.currentUser = response;
      this.authStatusSubject.next({
        isLoggedIn: true,
        user: {
          id: response.id,
          name: response.name,
          email: response.email,
        },
        isLoading: false,
      });

      console.log("User authenticated:", response);
    });
  }

  // Public method: Login with Facebook
  public async login(): Promise<boolean> {
    if (!this.isSDKLoaded) {
      await this.waitForSDK();
    }

    this.authStatusSubject.next({
      ...this.authStatusSubject.value,
      isLoading: true,
    });

    return new Promise((resolve) => {
      FB.login(
        (response: FacebookAuthResponse) => {
          console.log("Login response:", response);

          if (response.authResponse) {
            this.handleAuthResponse(response);
            resolve(true);
          } else {
            this.authStatusSubject.next({
              isLoggedIn: false,
              error: "Login was cancelled or not fully authorized",
              isLoading: false,
            });
            resolve(false);
          }
        },
        {
          scope: this.REQUIRED_PERMISSIONS.join(","),
          return_scopes: true,
        }
      );
    });
  }

  // Public method: Logout from Facebook
  public async logout(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.isSDKLoaded || !this.authStatusSubject.value.isLoggedIn) {
        this.resetState();
        resolve();
        return;
      }

      FB.logout(() => {
        console.log("Logged out successfully");
        this.resetState();
        resolve();
      });
    });
  }

  // Reset all state
  private resetState(): void {
    this.currentAccessToken = null;
    this.currentUser = null;
    this.authStatusSubject.next({ isLoggedIn: false, isLoading: false });
    this.instagramAccountsSubject.next([]);
    this.facebookPagesSubject.next([]);
  }

  // Wait for SDK to load
  private waitForSDK(): Promise<void> {
    return new Promise((resolve) => {
      const checkSDK = () => {
        if (this.isSDKLoaded) {
          resolve();
        } else {
          setTimeout(checkSDK, 100);
        }
      };
      checkSDK();
    });
  }

  // Get user's Facebook pages
  public async getFacebookPages(): Promise<FacebookPage[]> {
    if (!this.isSDKLoaded || !this.currentAccessToken) {
      throw new Error("Not authenticated or SDK not loaded");
    }

    return new Promise((resolve, reject) => {
      FB.api(
        "/me/accounts",
        { access_token: this.currentAccessToken },
        (response: any) => {
          if (response.error) {
            console.error("Error getting Facebook pages:", response.error);
            reject(
              new Error(
                `Failed to get Facebook pages: ${response.error.message}`
              )
            );
            return;
          }

          const pages: FacebookPage[] = response.data || [];
          this.facebookPagesSubject.next(pages);
          console.log("Facebook pages retrieved:", pages);
          resolve(pages);
        }
      );
    });
  }

  // Get Instagram business account for a specific Facebook page
  private getInstagramAccountForPage(page: FacebookPage): Promise<any> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${page.id}`,
        {
          fields: "instagram_business_account",
          access_token: page.access_token,
        },
        (response: any) => {
          if (response.error) {
            reject(
              new Error(
                `Error getting Instagram account: ${response.error.message}`
              )
            );
            return;
          }

          if (response.instagram_business_account) {
            resolve(response.instagram_business_account);
          } else {
            reject(
              new Error("No Instagram business account connected to this page")
            );
          }
        }
      );
    });
  }

  // Get Instagram account details
  private async getInstagramAccountDetails(
    instagramId: string,
    accessToken: string
  ): Promise<any> {
    try {
      const response = await fetch(
        `https://graph.instagram.com/${this.API_VERSION}/${instagramId}?` +
          `fields=id,username,account_type&access_token=${accessToken}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      return data;
    } catch (error) {
      console.error("Error getting Instagram account details:", error);
      throw error;
    }
  }

  // Public method: Get all Instagram business accounts
  public async getInstagramAccounts(): Promise<InstagramAccount[]> {
    try {
      console.log("Getting Instagram accounts...");

      const pages = await this.getFacebookPages();
      const instagramAccounts: InstagramAccount[] = [];

      // Check each page for Instagram Business Account
      for (const page of pages) {
        try {
          console.log(`Checking page: ${page.name}`);

          const instagramAccount = await this.getInstagramAccountForPage(page);

          if (instagramAccount) {
            // Get additional Instagram account details
            try {
              const details = await this.getInstagramAccountDetails(
                instagramAccount.id,
                page.access_token
              );

              instagramAccounts.push({
                pageName: page.name,
                pageId: page.id,
                pageAccessToken: page.access_token,
                instagramAccountId: instagramAccount.id,
                username: details.username,
                accountType: details.account_type,
              });

              console.log(`Found Instagram account: ${details.username}`);
            } catch (detailsError) {
              // Still add the account even if we can't get details
              instagramAccounts.push({
                pageName: page.name,
                pageId: page.id,
                pageAccessToken: page.access_token,
                instagramAccountId: instagramAccount.id,
              });

              console.warn(
                `Could not get details for Instagram account ${instagramAccount.id}:`,
                detailsError
              );
            }
          }
        } catch (error) {
          console.warn(`No Instagram account for page ${page.name}:`, error);
        }
      }

      this.instagramAccountsSubject.next(instagramAccounts);
      console.log("Instagram accounts found:", instagramAccounts);
      return instagramAccounts;
    } catch (error) {
      console.error("Error getting Instagram accounts:", error);
      throw error;
    }
  }

  // Public method: Test Instagram API connection
  public async testInstagramConnection(
    account: InstagramAccount
  ): Promise<any> {
    try {
      const response = await fetch(
        `https://graph.instagram.com/${this.API_VERSION}/${account.instagramAccountId}?` +
          `fields=id,username,account_type,media_count&access_token=${account.pageAccessToken}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      console.log("Instagram connection test successful:", data);
      return data;
    } catch (error) {
      console.error("Instagram connection test failed:", error);
      throw error;
    }
  }

  // Public method: Check if user has required permissions
  public async checkPermissions(): Promise<string[]> {
    if (!this.currentAccessToken) {
      throw new Error("Not authenticated");
    }

    return new Promise((resolve, reject) => {
      FB.api(
        "/me/permissions",
        { access_token: this.currentAccessToken },
        (response: any) => {
          if (response.error) {
            reject(
              new Error(
                `Failed to check permissions: ${response.error.message}`
              )
            );
            return;
          }

          const grantedPermissions = response.data
            .filter((perm: any) => perm.status === "granted")
            .map((perm: any) => perm.permission);

          console.log("Granted permissions:", grantedPermissions);
          resolve(grantedPermissions);
        }
      );
    });
  }

  // Getters for current state
  public getCurrentAccessToken(): string | null {
    return this.currentAccessToken;
  }

  public getCurrentUser(): any {
    return this.currentUser;
  }

  public getInstagramAccountsValue(): InstagramAccount[] {
    return this.instagramAccountsSubject.value;
  }

  public getFacebookPagesValue(): FacebookPage[] {
    return this.facebookPagesSubject.value;
  }

  public isAuthenticated(): boolean {
    return this.authStatusSubject.value.isLoggedIn;
  }

  // Utility method: Get long-lived access token
  public async getLongLivedToken(): Promise<{
    access_token: string;
    expires_in: number;
  }> {
    if (!this.currentAccessToken) {
      throw new Error("No current access token available");
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/${this.API_VERSION}/oauth/access_token?` +
          `grant_type=fb_exchange_token&` +
          `client_id=${this.FB_APP_ID}&` +
          `client_secret=YOUR_APP_SECRET&` + // You'll need to handle this securely
          `fb_exchange_token=${this.currentAccessToken}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      return data;
    } catch (error) {
      console.error("Error getting long-lived token:", error);
      throw error;
    }
  }
}
